import axios, { AxiosResponse } from 'axios';
import { ApiResponse, PaginatedResponse } from '@/types';

// Use relative path for API requests (will be handled by Vite proxy)
export const BASE_URL = '';

// Create axios instance
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'ngrok-skip-browser-warning': 'true'
  },
  withCredentials: true // Required for cookies/session handling
});

// Add request interceptor for CORS preflight
api.interceptors.request.use((config) => {
  // Add CORS headers
  config.headers['Access-Control-Allow-Origin'] = window.location.origin;
  config.headers['Access-Control-Allow-Methods'] = 'GET, POST, PUT, DELETE, OPTIONS';
  config.headers['Access-Control-Allow-Headers'] = 'Content-Type, Authorization, X-Requested-With, ngrok-skip-browser-warning';
  config.headers['Access-Control-Allow-Credentials'] = 'true';
  return config;
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 419) {
      // CSRF token mismatch, try to refresh
      await getCsrfToken();
      // Retry the original request
      return api.request(error.config);
    }
    return Promise.reject(error);
  }
);

// Function to get CSRF token
export const getCsrfToken = async () => {
  try {
    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();
    
    // Make the request to sanctum/csrf-cookie endpoint
    await axios.get(`${BASE_URL}/sanctum/csrf-cookie?t=${timestamp}`, {
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'ngrok-skip-browser-warning': 'true'
      }
    });
    
    // Get XSRF token from cookie
    const xsrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('XSRF-TOKEN='))
      ?.split('=')[1];

    if (xsrfToken) {
      // Set it for all future requests
      api.defaults.headers.common['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);
      console.log('✅ CSRF token set:', decodeURIComponent(xsrfToken));
    } else {
      console.warn('⚠️ No XSRF-TOKEN cookie found');
    }
    
    return true;
  } catch (error) {
    console.error('CSRF token fetch error:', error);
    return false;
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default api;

// Helper function for handling API responses
export const handleApiResponse = <T>(response: AxiosResponse<any>): T => {
  // Only log in development mode
  if (import.meta.env.DEV) {
    console.log('📦 Processing API response:', response.data);
  }
  
  // Handle different response formats
  if (response.data.success === true || response.data.status === 'success') {
    return response.data.data;
  }
  
  // If response is successful but doesn't have success flag, return the data directly
  if (response.status >= 200 && response.status < 300) {
    // Some endpoints might return data directly without wrapping
    if (response.data.data) {
      return response.data.data;
    }
    return response.data;
  }
  
  throw new Error(response.data.message || 'API request failed');
};

// Helper function for handling paginated responses
export const handlePaginatedResponse = <T>(
  response: AxiosResponse<any>
): PaginatedResponse<T> => {
  // If response.data is already the paginated data structure
  if (response.data?.data && Array.isArray(response.data.data) && 
      'current_page' in response.data && 
      'total' in response.data && 
      'per_page' in response.data && 
      'last_page' in response.data) {
    return response.data;
  }
  
  // If response.data.data is the paginated data structure
  if (response.data?.data?.data && Array.isArray(response.data.data.data) && 
      'current_page' in response.data.data && 
      'total' in response.data.data && 
      'per_page' in response.data.data && 
      'last_page' in response.data.data) {
    return response.data.data;
  }
  
  // If response.data is just an array
  if (Array.isArray(response.data)) {
    return {
      data: response.data,
      current_page: 1,
      per_page: response.data.length,
      total: response.data.length,
      last_page: 1
    };
  }

  // If response.data.success is true and data is an array
  if (response.data?.success && Array.isArray(response.data?.data)) {
    return {
      data: response.data.data,
      current_page: 1,
      per_page: response.data.data.length,
      total: response.data.data.length,
      last_page: 1
    };
  }

  throw new Error(response.data?.message || 'API request failed');
};

// API Health Check function
export const checkApiHealth = async (baseUrl?: string) => {
  const testUrl = baseUrl || BASE_URL;
  try {
    console.log('🏥 Checking API health at:', testUrl);
    
    // Test basic connectivity
    const response = await fetch(testUrl.replace('/api', '/api/user'), {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
        'Authorization': `Bearer ${localStorage.getItem('auth_token') || 'no-token'}`
      }
    });
    
    console.log('API Health Response Status:', response.status);
    const data = await response.text();
    console.log('API Health Response:', data);
    
    return {
      success: response.ok,
      status: response.status,
      url: testUrl,
      response: data
    };
  } catch (error) {
    console.error('API Health Check Failed:', error);
    return {
      success: false,
      status: 0,
      url: testUrl,
      error: error.message
    };
  }
};

// === MEMBER SELF-SERVICE API ===
export const memberApi = {
  // Get member dashboard data
  getDashboard: async () => {
    const response = await api.get('/api/members/dashboard');
    return handleApiResponse(response);
  },

  // Get member's own payments
  getMyPayments: async () => {
    const response = await api.get('/api/my-payments');
    return handleApiResponse(response);
  },

  // Get member's own settings
  getMySettings: async () => {
    const response = await api.get('/api/my-settings');
    return handleApiResponse(response);
  },

  // Update member's own profile
  updateMyProfile: async (profileData: any) => {
    const response = await api.put('/api/members/profile', profileData);
    return handleApiResponse(response);
  },

  // Get member's photo info
  getMyPhoto: async () => {
    const response = await api.get('/api/members/photo');
    return handleApiResponse(response);
  },

  // Upload member's photo
  uploadMyPhoto: async (photoFile: File) => {
    const formData = new FormData();
    formData.append('photo', photoFile);
    const response = await api.post('/api/members/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return handleApiResponse(response);
  },

  // Delete member's photo
  deleteMyPhoto: async () => {
    const response = await api.delete('/api/members/photo');
    return handleApiResponse(response);
  },

  // Get member's digital card
  getMyDigitalCard: async () => {
    const response = await api.get('/api/my-digital-card');
    return handleApiResponse(response);
  },

  // Download member's digital card
  downloadMyDigitalCard: async () => {
    const response = await api.get('/api/my-digital-card/download', {
      responseType: 'blob'
    });
    return response.data;
  },

  // Email member's digital card
  emailMyDigitalCard: async (email: string, message?: string) => {
    const response = await api.post('/api/my-digital-card/email', { email, message });
    return handleApiResponse(response);
  },

  // Get member's preferences
  getMyPreferences: async () => {
    const response = await api.get('/my-preferences');
    return handleApiResponse(response);
  },

  // Update member's preferences
  updateMyPreferences: async (preferences: any) => {
    const response = await api.put('/my-preferences', preferences);
    return handleApiResponse(response);
  },

  // Update registration endpoint to /api/register
  register: async (formData: FormData) => {
    const response = await api.post('/api/register', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return handleApiResponse(response);
  }
};

// === MEMBERS API ===
export const membersApi = {
  // Get all members
  getAll: async (params?: any) => {
    const response = await api.get('/api/members', { params });
    return handlePaginatedResponse(response);
  },

  // Get member by ID
  getById: async (id: number) => {
    const response = await api.get(`/api/members/${id}`);
    return handleApiResponse(response);
  },

  // Create member
  create: async (memberData: any) => {
    const response = await api.post('/api/members', memberData);
    return handleApiResponse(response);
  },

  // Update member
  update: async (id: number, memberData: any) => {
    const response = await api.put(`/api/members/${id}`, memberData);
    return handleApiResponse(response);
  },

  // Delete member
  delete: async (id: number) => {
    const response = await api.delete(`/api/members/${id}`);
    return handleApiResponse(response);
  },

  // Approve member
  approve: async (id: number) => {
    const response = await api.post(`/api/members/${id}/approve`);
    return handleApiResponse(response);
  },

  // Reject member
  reject: async (id: number, reason?: string) => {
    const response = await api.post(`/api/members/${id}/reject`, { reason });
    return handleApiResponse(response);
  },

  // Suspend member
  suspend: async (id: number, reason?: string) => {
    const response = await api.post(`/api/members/${id}/suspend`, { reason });
    return handleApiResponse(response);
  },

  // Disable member
  disable: async (id: number, reason?: string) => {
    const response = await api.post(`/api/members/${id}/disable`, { reason });
    return handleApiResponse(response);
  },

  // Reactivate member
  reactivate: async (id: number) => {
    const response = await api.post(`/api/members/${id}/reactivate`);
    return handleApiResponse(response);
  },

  // Get member dashboard (admin view)
  getDashboard: async (id: number) => {
    const response = await api.get(`/api/members/dashboard/${id}`);
    return handleApiResponse(response);
  },

  // Get member payments
  getPayments: async (id: number) => {
    const response = await api.get(`/api/members/${id}/payments`);
    return handleApiResponse(response);
  },

  // Get photo info for specific member (admin)
  getPhotoInfo: async (id: number) => {
    const response = await api.get(`/api/members/photo/${id}`);
    return handleApiResponse(response);
  },

  // Upload photo for specific member (admin)
  uploadPhoto: async (id: number, photoFile: File) => {
    const formData = new FormData();
    formData.append('photo', photoFile);
    const response = await api.post(`/api/members/photo/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return handleApiResponse(response);
  },

  // Get member's digital card (admin)
  getDigitalCard: async (id: number) => {
    const response = await api.get(`/api/members/${id}/digital-card`);
    return handleApiResponse(response);
  }
};

// === PAYMENTS API ===
export const paymentsApi = {
  // Get all payments
  getAll: async (params?: any) => {
    const response = await api.get('/api/payments', { params });
    return handlePaginatedResponse(response);
  },

  // Get payment by ID
  getById: async (id: number) => {
    const response = await api.get(`/api/payments/${id}`);
    return handleApiResponse(response);
  },

  // Create payment
  create: async (paymentData: any) => {
    const response = await api.post('/payments', paymentData);
    return handleApiResponse(response);
  },

  // Update payment
  update: async (id: number, paymentData: any) => {
    const response = await api.put(`/payments/${id}`, paymentData);
    return handleApiResponse(response);
  },

  // Delete payment
  delete: async (id: number) => {
    const response = await api.delete(`/payments/${id}`);
    return handleApiResponse(response);
  },

  // Initiate payment
  initiate: async (paymentData: {
    member_id: number;
    amount: number;
    phone: string;
    provider: string;
    description: string;
  }) => {
    const response = await api.post('/payments/initiate', paymentData);
    return handleApiResponse(response);
  },

  // Get payment status
  getStatus: async (transactionId: string) => {
    const response = await api.get(`/api/payments/status/${transactionId}`);
    return handleApiResponse(response);
  },

  // Get payment config status
  getConfigStatus: async () => {
    const response = await api.get('/payments/config-status');
    return handleApiResponse(response);
  }
};

// === REPORTS API ===
export const reportsApi = {
  // Get payments datatable report
  getPaymentsDataTable: async () => {
    const response = await api.get('/api/reports/payments/datatable');
    return handleApiResponse(response);
  },

  // Get payments summary report
  getPaymentsSummary: async () => {
    const response = await api.get('/api/reports/payments/summary');
    return handleApiResponse(response);
  },

  // Get quick reports
  getQuickReports: async () => {
    const response = await api.get('/api/reports/quick-reports');
    return handleApiResponse(response);
  },

  // Get member details report
  getMemberDetails: async (filters?: any) => {
    const response = await api.get('/api/reports/member-details', { params: filters });
    return handleApiResponse(response);
  },

  // Get payment history report
  getPaymentHistory: async (filters?: any) => {
    const response = await api.get('/api/reports/payment-history', { params: filters });
    return handleApiResponse(response);
  },

  // Generate financial summary report
  getFinancialSummary: async (timeRange: string = '30d') => {
    const response = await api.get(`/api/reports/financial-summary?time_range=${timeRange}`);
    return handleApiResponse(response);
  },

  // Generate activity report
  getActivityReport: async (timeRange: string = '30d') => {
    const response = await api.get(`/api/reports/activity?time_range=${timeRange}`);
    return handleApiResponse(response);
  },

  // Generate monthly summary report
  getMonthlySummary: async (year?: number, month?: number) => {
    const params = year && month ? `?year=${year}&month=${month}` : '';
    const response = await api.get(`/api/reports/monthly-summary${params}`);
    return handleApiResponse(response);
  },

  // Export custom report
  exportCustomReport: async (reportType: string, filters?: any, format: string = 'pdf') => {
    const response = await api.get(`/api/reports/export/${reportType}`, {
      params: { ...filters, format },
      responseType: 'blob'
    });
    return response.data;
  },

  // Export data table report (used in payments datatable)
  exportDataTableReport: async (type: string, filters?: any) => {
    const response = await api.get(`/api/reports/${type}/datatable`, { params: filters });
    return handleApiResponse(response);
  }
};

// === SMS API ===
export const smsApi = {
  // Send SMS to a single number
  sendSingle: async (phone: string, message: string) => {
    try {
      const response = await api.post('/api/sms/send', { phone, message });
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('SMS API Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        fullError: error
      });
      
      // Let the backend error message through without modification for debugging
      const backendMessage = error.response?.data?.message || error.message;
      console.log('Backend error message:', backendMessage);
      
      // For now, just pass through the actual error
      throw new Error(backendMessage || 'Failed to send SMS');
    }
  },

  // Send SMS to multiple numbers
  sendBulk: async (phones: string[], message: string) => {
    try {
      const response = await api.post('/api/sms/bulk', { phones, message });
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('SMS Bulk API Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      const backendMessage = error.response?.data?.message || error.message;
      throw new Error(backendMessage || 'Failed to send bulk SMS');
    }
  },

  // Send SMS to filtered group
  sendFiltered: async (filters: any, message: string) => {
    try {
      const response = await api.post('/api/sms/filtered', { filters, message });
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('SMS Filtered API Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      const backendMessage = error.response?.data?.message || error.message;
      throw new Error(backendMessage || 'Failed to send filtered SMS');
    }
  },

  // Get SMS logs
  getLogs: async () => {
    try {
      const response = await api.get('/api/sms/logs');
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('SMS API Error:', error.response || error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to get SMS logs');
    }
  },

  // Get SMS statistics
  getStats: async () => {
    try {
      const response = await api.get('/api/sms/stats');
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('SMS API Error:', error.response || error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to get SMS stats');
    }
  },

  // Get SMS configuration status - use environment config instead
  getConfigStatus: async () => {
    try {
      // First try the original endpoint
      const response = await api.get('/api/sms/config-status');
      return handleApiResponse(response);
    } catch (error: any) {
      console.warn('SMS config status endpoint failed, trying environment config:', error);
      
      // Fallback to environment config
      try {
        const envConfig = await api.get('/api/environment/sms-config');
        const configData = handleApiResponse(envConfig) as any;
        
        // Transform environment config to status format
        return {
          enabled: !!(configData.sms_base_url && configData.sms_identifier && configData.sms_password && configData.sms_enabled),
          base_url: configData.sms_base_url,
          identifier: configData.sms_identifier,
          sender_name: configData.sms_sender_name
        };
      } catch (envError: any) {
        console.error('Both SMS config endpoints failed:', error, envError);
        // Return default disabled status
        return {
          enabled: false,
          message: 'SMS configuration not found'
        };
      }
    }
  }
};

// === DIGITAL CARDS API ===
export const digitalCardsApi = {
  getStatistics: async () => {
    const response = await api.get('/api/digital-cards/statistics');
    return handleApiResponse(response);
  },

  getAll: async (search = '', perPage = 15) => {
    const response = await api.get(`/api/digital-cards?search=${search}&per_page=${perPage}`);
    return handlePaginatedResponse(response);
  },

  getById: async (id: number) => {
    const response = await api.get(`/api/digital-cards/${id}`);
    return handleApiResponse(response);
  },

  generate: async (memberId: number) => {
    const response = await api.post('/api/digital-cards/generate', { member_id: memberId });
    return handleApiResponse(response);
  },

  generateBulk: async (memberIds: number[]) => {
    const response = await api.post('/api/digital-cards/generate-bulk', { member_ids: memberIds });
    return handleApiResponse(response);
  },

  validateQr: async (qrData: string) => {
    const response = await api.post('/api/digital-cards/validate-qr', { qr_data: qrData });
    return handleApiResponse(response);
  },

  getMemberCard: async (memberId: number) => {
    const response = await api.get(`/api/members/${memberId}/digital-card`);
    return handleApiResponse(response);
  },
  
  getMyCard: async () => {
    const response = await api.get('/api/my-digital-card');
    return handleApiResponse(response);
  },
  
  downloadCard: async (cardId: number) => {
    const response = await api.get(`/api/digital-cards/${cardId}/download`, {
      responseType: 'blob', // Important for file downloads
    });
    return response.data;
  },

  downloadBulk: async (cardIds: number[]) => {
    const response = await api.post('/api/digital-cards/download-bulk', 
      { card_ids: cardIds }, 
      { 
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      }
    );
    return response.data;
  },

  emailCard: async (cardId: number, email: string) => {
    const response = await api.post('/api/digital-cards/email', { card_id: cardId, email });
    return handleApiResponse(response);
  },
  
  getPremiumCard: async (cardId: number) => {
    const response = await api.get(`/api/digital-cards/premium/${cardId}`, {
      headers: {
        'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
      }
    });
    return handleApiResponse(response);
  },

  getMyPremiumCard: async () => {
    const response = await api.get('/api/my-premium-card', {
      headers: { 'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8' },
    });
    return response.data; // Return raw HTML
  },

  // Download member's own digital card
  downloadMyCard: async () => {
    const response = await api.get('/api/my-digital-card/download', {
      responseType: 'blob', // Important for file downloads
    });
    return response.data;
  },

  // Email member's own digital card
  emailMyCard: async (email: string) => {
    const response = await api.post('/api/my-digital-card/email', { email });
    return handleApiResponse(response);
  },

  // Admin: Download a specific digital card by ID
  downloadAdminCard: async (cardId: number) => {
    const response = await api.get(`/api/digital-cards/${cardId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Admin: Email a specific digital card by ID
  emailAdminCard: async (cardId: number, email: string, message?: string) => {
    const response = await api.post('/api/digital-cards/email', { card_id: cardId, email, message });
    return handleApiResponse(response);
  },
};

// === SETTINGS API ===
export const settingsApi = {
  // Get all settings
  getAll: async () => {
    const response = await api.get('/api/settings');
    return handleApiResponse(response);
  },

  // Update system settings
  update: async (settings: any) => {
    const response = await api.put('/api/settings', settings);
    return handleApiResponse(response);
  },

  // Test SMS configuration - try multiple endpoints
  testSMS: async (phone: string, message: string) => {
    try {
      // First try the settings endpoint
      const response = await api.post('/api/settings/test-sms', { phone, message });
      return handleApiResponse(response);
    } catch (error: any) {
      // If that fails, try the environment config endpoint
      if (error.response?.status === 400 || error.response?.status === 404) {
        try {
          const response = await api.post('/api/environment/sms-config/test', { phone, message });
          return handleApiResponse(response);
        } catch (envError: any) {
          console.error('Both SMS test endpoints failed:', error, envError);
          throw new Error('SMS test failed. Please check your SMS configuration.');
        }
      }
      throw error;
    }
  },

  // Get email templates
  getEmailTemplates: async () => {
    try {
      const response = await api.get('/api/settings/email-templates');
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('Failed to fetch email templates:', error);
      // Return empty array if templates not found
      if (error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  // Update email template
  updateEmailTemplate: async (templateData: any) => {
    try {
      const response = await api.put('/api/settings/email-templates', templateData);
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('Failed to update email template:', error);
      if (error.response?.status === 422) {
        const validationErrors = error.response.data?.errors;
        if (validationErrors) {
          const firstError = Object.values(validationErrors)[0];
          throw new Error(Array.isArray(firstError) ? firstError[0] : firstError as string);
        }
        throw new Error('Validation failed. Please check the template data.');
      }
      throw new Error(error.response?.data?.message || error.message || 'Failed to update email template');
    }
  }
};

// === USER SETTINGS API ===
export const userSettingsApi = {
  // Get all settings
  getAll: async () => {
    const response = await api.get('/api/user-settings');
    return handleApiResponse(response);
  },

  // Get user settings
  getSettings: async () => {
    const response = await api.get('/api/user-settings');
    return handleApiResponse(response);
  },

  // Update user settings
  updateSettings: async (settings: any) => {
    const response = await api.put('/api/user-settings', settings);
    return handleApiResponse(response);
  },

  // Get user preferences
  getPreferences: async () => {
    const response = await api.get('/api/my-preferences');
    return handleApiResponse(response);
  },

  // Update user preferences
  updatePreferences: async (preferences: any) => {
    const response = await api.put('/api/my-preferences', preferences);
    return handleApiResponse(response);
  }
};

// === MEMBER TYPES API ===
export const adminMemberTypesApi = {
  // Get all member types
  getAll: async () => {
    const response = await api.get('/api/admin/member-types');
    return handleApiResponse(response);
  },

  // Get member type by ID
  getById: async (id: number) => {
    const response = await api.get(`/api/admin/member-types/${id}`);
    return handleApiResponse(response);
  },

  // Create member type
  create: async (data: any) => {
    const response = await api.post('/api/admin/member-types', data);
    return handleApiResponse(response);
  },

  // Update member type
  update: async (id: number, data: any) => {
    const response = await api.put(`/api/admin/member-types/${id}`, data);
    return handleApiResponse(response);
  },

  // Delete member type
  delete: async (id: number) => {
    const response = await api.delete(`/api/admin/member-types/${id}`);
    return handleApiResponse(response);
  }
};

// === PLAYING LEVELS API ===
export const playingLevelsApi = {
  // Get all playing levels
  getAll: async () => {
    const response = await api.get('/api/admin/playing-levels');
    return handleApiResponse(response);
  },

  // Get playing level by ID
  getById: async (id: number) => {
    const response = await api.get(`/api/admin/playing-levels/${id}`);
    return handleApiResponse(response);
  },

  // Create playing level
  create: async (data: any) => {
    const response = await api.post('/api/admin/playing-levels', data);
    return handleApiResponse(response);
  },

  // Update playing level
  update: async (id: number, data: any) => {
    const response = await api.put(`/api/admin/playing-levels/${id}`, data);
    return handleApiResponse(response);
  },

  // Delete playing level
  delete: async (id: number) => {
    const response = await api.delete(`/api/admin/playing-levels/${id}`);
    return handleApiResponse(response);
  },

  // Toggle playing level status
  toggleStatus: async (id: number) => {
    const response = await api.post(`/api/admin/playing-levels/${id}/toggle-status`);
    return handleApiResponse(response);
  }
};

// === PLAYING POSITIONS API ===
export const playingPositionsApi = {
  // Get all playing positions
  getAll: async () => {
    const response = await api.get('/api/admin/playing-positions');
    return handleApiResponse(response);
  },

  // Get playing position by ID
  getById: async (id: number) => {
    const response = await api.get(`/api/admin/playing-positions/${id}`);
    return handleApiResponse(response);
  },

  // Create playing position
  create: async (data: any) => {
    const response = await api.post('/api/admin/playing-positions', data);
    return handleApiResponse(response);
  },

  // Update playing position
  update: async (id: number, data: any) => {
    const response = await api.put(`/api/admin/playing-positions/${id}`, data);
    return handleApiResponse(response);
  },

  // Delete playing position
  delete: async (id: number) => {
    const response = await api.delete(`/api/admin/playing-positions/${id}`);
    return handleApiResponse(response);
  },

  // Toggle playing position status
  toggleStatus: async (id: number) => {
    const response = await api.post(`/api/admin/playing-positions/${id}/toggle-status`);
    return handleApiResponse(response);
  }
};

// === MEMBER PREMIUM CARD API ===
export const premiumCardApi = {
  // Get my premium card (HTML view)
  getMyPremiumCard: async () => {
    const response = await api.get('/api/my-premium-card', {
      headers: { 'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8' }
    });
    return response.data; // Return raw HTML
  }
};

// === ANALYTICS & REPORTS API ===
export const analyticsApi = {
  // Get dashboard statistics
  getDashboardStats: async () => {
    const response = await api.get('/analytics/dashboard-stats');
    return handleApiResponse(response);
  },

  // Get analytics data
  getAnalyticsData: async (timeRange: string = '30d') => {
    const response = await api.get(`/api/analytics/data?time_range=${timeRange}`);
    return handleApiResponse(response);
  },

  // Get membership growth chart data
  getMembershipGrowth: async (months: number = 6) => {
    const response = await api.get(`/api/analytics/membership-growth?months=${months}`);
    return handleApiResponse(response);
  },

  // Get member types distribution
  getMemberTypesDistribution: async () => {
    const response = await api.get('/analytics/member-types-distribution');
    return handleApiResponse(response);
  },

  // Get revenue trends
  getRevenueTrends: async (months: number = 6) => {
    const response = await api.get(`/api/analytics/revenue-trends?months=${months}`);
    return handleApiResponse(response);
  },

  // Get payment analytics
  getPaymentAnalytics: async (timeRange: string = '30d') => {
    const response = await api.get(`/api/analytics/payments?time_range=${timeRange}`);
    return handleApiResponse(response);
  },

  // Get SMS analytics
  getSMSAnalytics: async (timeRange: string = '30d') => {
    const response = await api.get(`/api/analytics/sms?time_range=${timeRange}`);
    return handleApiResponse(response);
  }
};

// === DASHBOARD API ===
export const dashboardApi = {
  // Get dashboard data
  getData: async () => {
    const response = await api.get('/api/dashboard');
    return handleApiResponse(response);
  },

  // Get my premium card
  getMyPremiumCard: async () => {
    const response = await api.get('/api/my-premium-card', {
      headers: { 'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8' }
    });
    return handleApiResponse(response);
  }
};

// === ADMIN API ===
export const adminApi = {
  // Members Management
  getMembers: async (params?: any) => {
    const response = await api.get('/api/members', { params });
    return handlePaginatedResponse(response);
  },

  getMember: async (id: number) => {
    const response = await api.get(`/api/members/${id}`);
    return handleApiResponse(response);
  },

  createMember: async (memberData: any) => {
    const response = await api.post('/api/members', memberData);
    return handleApiResponse(response);
  },

  updateMember: async (id: number, memberData: any) => {
    const response = await api.put(`/api/members/${id}`, memberData);
    return handleApiResponse(response);
  },

  deleteMember: async (id: number) => {
    const response = await api.delete(`/api/members/${id}`);
    return handleApiResponse(response);
  },

  // Get all payments
  getPayments: async (params?: any) => {
    const response = await api.get('/api/payments', { params });
    return handlePaginatedResponse(response);
  },

  // Get payment by ID
  getPayment: async (id: number) => {
    const response = await api.get(`/api/payments/${id}`);
    return handleApiResponse(response);
  },

  // Get payment status
  getPaymentStatus: async (transactionId: string) => {
    const response = await api.get(`/api/payments/status/${transactionId}`);
    return handleApiResponse(response);
  },

  // Member Types
  getMemberTypes: async () => {
    const response = await api.get('/api/admin/member-types');
    return handleApiResponse(response);
  },

  // Playing Positions
  getPlayingPositions: async () => {
    const response = await api.get('/api/admin/playing-positions');
    return handleApiResponse(response);
  },

  // Playing Levels
  getPlayingLevels: async () => {
    const response = await api.get('/api/admin/playing-levels');
    return handleApiResponse(response);
  },

  // SMS Management
  sendSingleSMS: async (data: { phone: string; message: string }) => {
    const response = await api.post('/api/sms/send', data);
    return handleApiResponse(response);
  },

  sendBulkSMS: async (data: { phones: string[]; message: string }) => {
    const response = await api.post('/api/sms/bulk', data);
    return handleApiResponse(response);
  },

  getSMSLogs: async () => {
    const response = await api.get('/api/sms/logs');
    return handleApiResponse(response);
  },

  getSMSStats: async () => {
    const response = await api.get('/api/sms/stats');
    return handleApiResponse(response);
  }
};

// === ENVIRONMENT CONFIGURATION API ===
export const environmentApi = {
  // Get SMS configuration
  getSMSConfig: async () => {
    try {
      const response = await api.get('/api/environment/sms-config');
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('Failed to get SMS config:', error);
      if (error.response?.status === 404) {
        return {
          sms_base_url: '',
          sms_identifier: '',
          sms_password: '',
          sms_enabled: false
        };
      }
      throw error;
    }
  },

  // Update SMS configuration
  updateSMSConfig: async (config: any) => {
    try {
      const response = await api.put('/api/environment/sms-config', config);
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('Failed to update SMS config:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to update SMS configuration');
    }
  },

  // Test SMS configuration
  testSMSConfig: async (phone: string, message: string) => {
    try {
      const response = await api.post('/api/environment/sms-config/test', { phone, message });
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('Failed to test SMS config:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to test SMS configuration');
    }
  },

  // Reset SMS configuration
  resetSMSConfig: async () => {
    try {
      const response = await api.delete('/api/environment/sms-config');
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('Failed to reset SMS config:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to reset SMS configuration');
    }
  }
};

// === PUBLIC MEMBER TYPES API ===
export const memberTypesApi = {
  // Get all member types
  getAll: async () => {
    const response = await api.get('/api/member-types');
    return handleApiResponse(response);
  }
};

// === PUBLIC PLAYING LEVELS API ===
export const publicPlayingLevelsApi = {
  // Get all playing levels
  getAll: async () => {
    const response = await api.get('/api/playing-levels');
    return handleApiResponse(response);
  }
};

// === PUBLIC PLAYING POSITIONS API ===
export const publicPlayingPositionsApi = {
  // Get all playing positions
  getAll: async () => {
    const response = await api.get('/api/playing-positions');
    return handleApiResponse(response);
  }
};

